// DeleteConfirmContext.tsx
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Modal, Text } from '@shopify/polaris';
import helpers from 'helpers/index';

interface DeleteConfirmContextProps {
  showDeleteConfirm: (
    title: string,
    content?: string,
    options?: {
      customAction?: {
        content: string;
        onAction: () => void | Promise<void>;
      };
    }
  ) => Promise<boolean>;
}

/**
 * Cách dùng
 * 
  const { showDeleteConfirm } = useDeleteConfirm();
  const deleteFaqCallback = useCallback(async () => {
    const confirmed = await showDeleteConfirm('Xóa record này?', 'Bạn có chắc muốn xóa không? Hành động này không thể khôi phục.');
    if (confirmed) {
      try {
      } catch (e) {}
    }
  }, []);

 */

const DeleteConfirmContext = createContext<DeleteConfirmContextProps | undefined>(undefined);

export const DeleteConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('Bạn chắc chắn chứ? Hành động này không thể khôi phục.');
  const [customAction, setCustomAction] = useState<{
    content: string;
    onAction: () => void | Promise<void>;
  } | null>(null);
  const [resolveCallback, setResolveCallback] = useState<(value: boolean) => void>(() => {});

  const showDeleteConfirm = useCallback(
    (
      title: string,
      content = 'Bạn chắc chắn chứ?',
      options?: {
        customAction?: {
          content: string;
          onAction: () => void | Promise<void>;
        };
      }
    ) => {
      setModalTitle(title);
      setModalContent(content);
      setCustomAction(options?.customAction ?? null);
      setModalOpen(true);
      return new Promise<boolean>((resolve) => {
        setResolveCallback(() => resolve);
      });
    },
    []
  );

  const handleClose = useCallback(() => {
    setModalOpen(false);
    resolveCallback(false);
  }, [resolveCallback]);

  const handleConfirm = useCallback(async () => {
    setModalOpen(false);
    await helpers.sleep(100); // giả lập tải
    resolveCallback(true);
  }, [resolveCallback]);

  const handleCustomAction = useCallback(async () => {
    if (!customAction) {
      return;
    }
    setModalOpen(true);
    await customAction.onAction();
    // resolveCallback(false);
  }, [customAction, resolveCallback]);

  return (
    <DeleteConfirmContext.Provider value={{ showDeleteConfirm }}>
      {children}
      <Modal
        open={isModalOpen}
        onClose={handleClose}
        title={modalTitle}
        primaryAction={{
          content: 'Tiếp tục?',
          onAction: handleConfirm,
        }}
        secondaryActions={[
          ...(customAction
            ? [
                {
                  content: customAction.content,
                  onAction: handleCustomAction,
                },
              ]
            : []),
          {
            content: 'Hủy',
            onAction: handleClose,
          },
        ]}
      >
        <Modal.Section>
          <Text as="p">{modalContent}</Text>
        </Modal.Section>
      </Modal>
    </DeleteConfirmContext.Provider>
  );
};

export const useDeleteConfirm = () => {
  const context = useContext(DeleteConfirmContext);
  if (!context) {
    throw new Error('useDeleteConfirm must be used within a DeleteConfirmProvider');
  }
  return context;
};

